import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  CircularProgress,
} from '@mui/material';
import { useApiClient } from '@/hooks/useApiClient';
import {
  People as PeopleIcon,
  Category as CategoryIcon,
  Description as DocumentIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

// Mock data - replace with actual API calls
const mockStats = {
  totalUsers: 156,
  activeUsers: 142,
  totalControls: 89,
  activeControls: 23,
  totalDocuments: 1247,
  systemHealth: {
    status: 'healthy' as const,
    database: { status: 'connected' as const, responseTime: 45 },
    storage: { status: 'healthy' as const, usagePercentage: 67 },
    uptime: 99.8,
  },
  recentActivity: [
    {
      id: '1',
      action: 'CONTROL_CREATE',
      resourceType: 'Control',
      resourceId: 'ctrl-123',
      timestamp: '2025-01-15T10:30:00Z',
      user: { id: 'u1', email: 'user@example.com' },
    },
    {
      id: '2', 
      action: 'USER_CREATE',
      resourceType: 'User',
      resourceId: 'usr-456',
      timestamp: '2025-01-15T09:15:00Z',
      user: { id: 'u2', email: 'admin@example.com' },
    },
    {
      id: '3',
      action: 'DOCUMENT_UPLOAD',
      resourceType: 'Document',
      resourceId: 'doc-789',
      timestamp: '2025-01-15T08:45:00Z',
      user: { id: 'u3', email: 'farmer@example.com' },
    },
  ],
};

const AdminDashboardPage: React.FC = () => {
  useApiClient(); // Inject current OIDC token into API client
  const [loading] = React.useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'success';
      case 'warning': return 'warning';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircleIcon />;
      case 'warning': return <WarningIcon />;
      case 'error': return <ErrorIcon />;
      default: return <InfoIcon />;
    }
  };

  const getActionLabel = (action: string) => {
    const actionLabels: Record<string, string> = {
      'CONTROL_CREATE': 'Kontroll skapad',
      'USER_CREATE': 'Användare skapad',
      'DOCUMENT_UPLOAD': 'Dokument uppladdat',
      'DOCUMENT_DELETE': 'Dokument borttaget',
      'CONTROL_UPDATE': 'Kontroll uppdaterad',
    };
    return actionLabels[action] || action;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh"
        }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
        Administratörsdashboard
      </Typography>

      <Typography
        variant="body1"
        sx={{
          color: "text.secondary",
          mb: 4
        }}>
        Översikt över systemets status och aktivitet
      </Typography>

      <Grid container spacing={3}>
        {/* Statistics Cards */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center"
                }}>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <PeopleIcon />
                </Avatar>
                <Box>
                  <Typography gutterBottom variant="body2" sx={{
                    color: "text.secondary"
                  }}>
                    Användare
                  </Typography>
                  <Typography variant="h5" sx={{
                    fontWeight: 600
                  }}>
                    {mockStats.totalUsers}
                  </Typography>
                  <Typography variant="body2" sx={{
                    color: "success.main"
                  }}>
                    {mockStats.activeUsers} aktiva
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center"
                }}>
                <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
                  <CategoryIcon />
                </Avatar>
                <Box>
                  <Typography gutterBottom variant="body2" sx={{
                    color: "text.secondary"
                  }}>
                    Kontroller
                  </Typography>
                  <Typography variant="h5" sx={{
                    fontWeight: 600
                  }}>
                    {mockStats.totalControls}
                  </Typography>
                  <Typography variant="body2" sx={{
                    color: "info.main"
                  }}>
                    {mockStats.activeControls} pågående
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center"
                }}>
                <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                  <DocumentIcon />
                </Avatar>
                <Box>
                  <Typography gutterBottom variant="body2" sx={{
                    color: "text.secondary"
                  }}>
                    Dokument
                  </Typography>
                  <Typography variant="h5" sx={{
                    fontWeight: 600
                  }}>
                    {mockStats.totalDocuments}
                  </Typography>
                  <Typography variant="body2" sx={{
                    color: "text.secondary"
                  }}>
                    Totalt uppladdade
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center"
                }}>
                <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                  <TrendingUpIcon />
                </Avatar>
                <Box>
                  <Typography gutterBottom variant="body2" sx={{
                    color: "text.secondary"
                  }}>
                    Systemhälsa
                  </Typography>
                  <Typography variant="h5" sx={{
                    fontWeight: 600
                  }}>
                    {mockStats.systemHealth.uptime}%
                  </Typography>
                  <Typography variant="body2" sx={{
                    color: "success.main"
                  }}>
                    Drifttid
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* System Health */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Systemhälsa
            </Typography>
            
            <Box sx={{ mt: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2
                }}>
                <Typography variant="body2">Övergripande status</Typography>
                <Chip
                  icon={getStatusIcon(mockStats.systemHealth.status)}
                  label={mockStats.systemHealth.status === 'healthy' ? 'Frisk' : 'Problem'}
                  color={getStatusColor(mockStats.systemHealth.status) as any}
                  size="small"
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2
                }}>
                <Typography variant="body2">Databas</Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1
                  }}>
                  <Typography variant="body2" sx={{
                    color: "text.secondary"
                  }}>
                    {mockStats.systemHealth.database.responseTime}ms
                  </Typography>
                  <Chip
                    label={mockStats.systemHealth.database.status === 'connected' ? 'Ansluten' : 'Frånkopplad'}
                    color={mockStats.systemHealth.database.status === 'connected' ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2
                }}>
                <Typography variant="body2">Lagringsutgäng</Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1
                  }}>
                  <Typography variant="body2" sx={{
                    color: "text.secondary"
                  }}>
                    {mockStats.systemHealth.storage.usagePercentage}%
                  </Typography>
                  <Chip
                    label={mockStats.systemHealth.storage.status === 'healthy' ? 'OK' : 'Varning'}
                    color={getStatusColor(mockStats.systemHealth.storage.status) as any}
                    size="small"
                  />
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Senaste aktivitet
            </Typography>
            
            <List sx={{ mt: 1 }}>
              {mockStats.recentActivity.map((activity) => (
                <ListItem key={activity.id} divider>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.light', width: 32, height: 32 }}>
                      <InfoIcon fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={getActionLabel(activity.action)}
                    secondary={
                      <>
                        <Typography component="span" variant="body2" sx={{
                          color: "text.secondary"
                        }}>
                          {activity.user?.email || 'System'}
                        </Typography>
                        {' • '}
                        <Typography component="span" variant="body2" sx={{
                          color: "text.secondary"
                        }}>
                          {new Date(activity.timestamp).toLocaleString('sv-SE', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboardPage;